import { useState, useEffect, useCallback, useMemo, useRef } from "preact/hooks";
import { ChatSidebar, ComposerHandle, UserMenuButton } from "./ChatSidebar";
import { PortalPreview } from "./PortalPreview";
import { ErrorBoundary } from "./ErrorBoundary";
import { FocusBlock } from "../lib/focusBlocks";
import { useNavigate } from "../contexts/AppContext";
import { useMediaQuery } from "../lib/useMediaQuery";

const MIN_SIDEBAR_WIDTH = 240;
const MAX_SIDEBAR_WIDTH = 700;
const DEFAULT_SIDEBAR_WIDTH = 380;

interface VersionsData {
  draft_version_id: number;
  published_version_id: number | null;
  has_unpublished_changes: boolean;
}

export function PortalView({ id }: { id: string }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [versions, setVersions] = useState<VersionsData | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishedFlash, setPublishedFlash] = useState(false);
  // FT-FOCUS: items the user pointed at, plus everything they COULD point at
  // (read off the rendered preview, which is the only place that knows what's
  // on screen and what it says). Both live here: the preview produces them and
  // the sidebar consumes them.
  const [focus, setFocus] = useState<FocusBlock[]>([]);
  const [items, setItems] = useState<FocusBlock[]>([]);
  // The @-menu's active row, mirrored into the preview as a highlight.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const composerRef = useRef<ComposerHandle | null>(null);

  // FT-MOBILE: below md breakpoint (768px) we show a single pane with a toggle.
  const isMobile = useMediaQuery("(max-width: 767.98px)");
  const [showMobilePane, setShowMobilePane] = useState<"chat" | "preview">(
    "chat",
  );
  // One-shot guard: after the first onMessagesChanged(true) fires to switch
  // the default pane to preview, subsequent calls are ignored so reattach
  // doesn't override the user's manual toggle.
  const autoPaneSet = useRef(false);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("chatSidebarWidth");
    const w = saved ? parseInt(saved, 10) : DEFAULT_SIDEBAR_WIDTH;
    return Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, w));
  });

  const fetchVersions = useCallback(() => {
    fetch(`/api/portals/${encodeURIComponent(id)}/versions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setVersions(data);
      })
      .catch(() => {});
  }, [id]);

  // Refresh tab title, iframe, and version status. Called on mount and after
  // chat-driven mutations so the preview reflects the latest portal state.
  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    fetch(`/api/portals/${encodeURIComponent(id)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then((data) => {
        setTitle(data.title || "");
        window.localStorage.setItem(`portal_slug_${id}`, data.slug);
      })
      .catch(() => setError("Portal not found"));
    fetchVersions();
  }, [id, fetchVersions]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Plain click selects just that block (and clicking the only selected block
  // again clears it); cmd/ctrl/shift-click toggles it into a multi-selection,
  // which is what "this text and the heading above it" needs.
  const handlePick = useCallback(
    (block: FocusBlock | null, additive: boolean) => {
      setFocus((prev) => {
        if (!block) return prev.length ? [] : prev;
        if (additive) {
          return prev.some((b) => b.id === block.id)
            ? prev.filter((b) => b.id !== block.id)
            : [...prev, block];
        }
        return prev.length === 1 && prev[0].id === block.id ? [] : [block];
      });
      // Pointing at something is the start of asking for a change, so put the
      // cursor where the request goes. Without this every pick costs a
      // deliberate click back into the composer before you can type.
      if (block) composerRef.current?.focus();
    },
    [],
  );

  // A chat turn can delete a block the user had selected. Drop picks that no
  // longer exist on each preview reload rather than sending a dangling id.
  const handleBlocksLoaded = useCallback((loaded: FocusBlock[]) => {
    setItems(loaded);
    const live = new Set(loaded.map((b) => b.id));
    setFocus((prev) => {
      const kept = prev.filter((b) => live.has(b.id));
      return kept.length === prev.length ? prev : kept;
    });
  }, []);

  const focusIds = useMemo(() => focus.map((b) => b.id), [focus]);

  useEffect(() => {
    const prev = document.title;
    document.title = title || "MyPortal";
    return () => {
      document.title = prev;
    };
  }, [title]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await fetch(
        `/api/portals/${encodeURIComponent(id)}/publish`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        },
      );
      if (res.ok) {
        fetchVersions();
        setPublishedFlash(true);
        setTimeout(() => setPublishedFlash(false), 2500);
      }
    } finally {
      setPublishing(false);
    }
  };

  const slug = window.localStorage.getItem(`portal_slug_${id}`) || "";
  const publicUrl = slug ? `${window.location.origin}/p/${slug}` : "";

  if (error) {
    return (
      <div class="min-vh-100 d-flex flex-column align-items-center justify-content-center p-4">
        <div class="text-center">
          <h1 class="display-4">404</h1>
          <p class="lead text-muted">{error}</p>
          <a
            class="btn btn-primary"
            href="/"
            onClick={(e: Event) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            <i class="bi bi-house"></i> Go Home
          </a>
        </div>
      </div>
    );
  }

  const handleResizeStart = (e: PointerEvent) => {
    e.preventDefault();
    const el = e.target as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    let finalWidth = startWidth;

    const handlePointerMove = (e: PointerEvent) => {
      const newWidth = startWidth + (e.clientX - startX);
      finalWidth = Math.max(
        MIN_SIDEBAR_WIDTH,
        Math.min(MAX_SIDEBAR_WIDTH, newWidth),
      );
      setSidebarWidth(finalWidth);
    };

    const handlePointerUp = () => {
      el.releasePointerCapture(e.pointerId);
      localStorage.setItem("chatSidebarWidth", String(finalWidth));
      el.removeEventListener("pointermove", handlePointerMove);
      el.removeEventListener("pointerup", handlePointerUp);
    };

    el.addEventListener("pointermove", handlePointerMove);
    el.addEventListener("pointerup", handlePointerUp);
  };

  const isPublished = versions != null && versions.published_version_id != null;
  // Publish is meaningful only when the draft differs from what's live.
  // Default to disabled until versions load (versions == null).
  const canPublish =
    versions != null && versions.has_unpublished_changes && !publishing;

  return (
    <div class="d-flex flex-column vh-100-safe overflow-hidden">
      {/* Unified top bar spanning full width */}
      <div class="hstack flex-shrink-0 gap-2 p-2 border-bottom bg-body">
        {/* Home — a real link so it's middle-clickable / openable in a new tab */}
        <a
          class="btn btn-outline-secondary btn-sm"
          href="/"
          onClick={(e: Event) => {
            e.preventDefault();
            navigate("/");
          }}
          title="Home"
        >
          <i class="bi bi-house"></i>
        </a>

        <strong class="text-truncate" style={{ maxWidth: isMobile ? "8rem" : "14rem" }}>
          {title ?? ""}
        </strong>

        {/* Right-hand group: publish status + actions + user menu. ms-auto
            pushes everything from here to the far right of the bar. */}
        {versions == null ? (
          <span class="text-muted small ms-auto">…</span>
        ) : isPublished ? (
          <a
            class="btn btn-sm btn-outline-success text-nowrap ms-auto"
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open the published page in a new tab"
          >
            <i class="bi bi-box-arrow-up-right me-1"></i>Open published
          </a>
        ) : (
          <span class="badge text-bg-secondary fw-normal text-nowrap ms-auto">
            Not published
          </span>
        )}

        {/* Publish button — enabled only when the draft has changes to push. */}
        <button
          type="button"
          class={`btn btn-sm text-nowrap ${
            publishedFlash
              ? "btn-success"
              : canPublish
                ? "btn-primary"
                : "btn-outline-secondary"
          }`}
          onClick={handlePublish}
          disabled={!canPublish}
          title={
            canPublish
              ? "Publish your latest changes"
              : isPublished
                ? "No changes to publish"
                : ""
          }
        >
          {publishing ? (
            <>
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {!isMobile && <span class="ms-1">Publishing…</span>}
            </>
          ) : publishedFlash ? (
            <>
              <i class="bi bi-check-lg"></i>
              {!isMobile && <span class="ms-1">Published</span>}
            </>
          ) : isPublished && !versions?.has_unpublished_changes ? (
            isMobile ? <i class="bi bi-check-lg"></i> : "Published"
          ) : isPublished ? (
            isMobile ? <i class="bi bi-upload"></i> : "Publish changes"
          ) : (
            isMobile ? <i class="bi bi-upload"></i> : "Publish"
          )}
        </button>

        {/* Open the DRAFT as a real page. The in-editor preview is a picking
            surface — clicks select instead of navigating and embeds are inert
            — so there has to be one click that gets you the working page.
            "Open published" above is not that: it shows the last publish, not
            what you're editing. */}
        <a
          class="btn btn-sm btn-outline-secondary"
          href={`/api/portals/${encodeURIComponent(id)}/preview`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open this draft in a new tab, with working links and embeds"
        >
          <i class="bi bi-eye"></i>
        </a>

        {/* Analytics */}
        <a
          class={`btn btn-sm btn-outline-secondary${isMobile ? " d-none d-md-inline-flex" : ""}`}
          href={`/analytics/${id}`}
          onClick={(e: Event) => {
            e.preventDefault();
            navigate(`/analytics/${id}`);
          }}
          title="Analytics"
        >
          <i class="bi bi-bar-chart"></i>
        </a>

        {/* User menu */}
        <UserMenuButton />
      </div>

      {/* Sidebar + resize handle + preview. Scoped ErrorBoundary so a
          crash in the preview iframe or chat sidebar shows an inline
          error instead of killing the whole editor shell. */}
      <ErrorBoundary>
        <div class="d-flex flex-grow-1 overflow-hidden">
        <div class={`${isMobile && showMobilePane !== "chat" ? "d-none" : ""} ${isMobile ? "flex-grow-1" : ""} overflow-hidden`}
          style={isMobile ? { minWidth: 0 } : undefined}>
          <ChatSidebar
            slug={slug}
            width={sidebarWidth}
            onPortalChanged={refresh}
            focus={focus}
            onFocusChange={setFocus}
            items={items}
            onPreviewItem={setPreviewId}
            composerRef={composerRef}
            mobile={isMobile}
            onMessagesChanged={(has) => {
              // Default to preview when returning to a portal with messages;
              // only set once on initial load — after that, manual switching only.
              if (has && !autoPaneSet.current) {
                autoPaneSet.current = true;
                setShowMobilePane("preview");
              }
            }}
          />
        </div>
          <div class="resize-handle" onPointerDown={handleResizeStart} />
          <div class={`flex-grow-1 overflow-hidden${isMobile && showMobilePane !== "preview" ? " d-none" : ""}`}>
            <PortalPreview
              id={id}
              refreshKey={refreshKey}
              focusIds={focusIds}
              previewId={previewId}
              onPick={handlePick}
              onBlocksLoaded={handleBlocksLoaded}
            />
          </div>
        </div>
      </ErrorBoundary>

      {/* FT-MOBILE: bottom tab bar — thumb-zone switcher, only below md.
          Sits outside the flex-grow content area so it's always pinned to
          the bottom of the safe-height shell. */}
      {isMobile && (
        <nav class="editor-tabbar">
          <button
            type="button"
            class={`editor-tab${showMobilePane === "chat" ? " active" : ""}`}
            onClick={() => setShowMobilePane("chat")}
          >
            <i class="bi bi-chat-left-text"></i>
            <span>Chat</span>
          </button>
          <button
            type="button"
            class={`editor-tab${showMobilePane === "preview" ? " active" : ""}`}
            onClick={() => setShowMobilePane("preview")}
          >
            <i class="bi bi-eye"></i>
            <span>Preview</span>
          </button>
        </nav>
      )}
    </div>
  );
}
